import { api } from './api';
import type { AuditActionValue, AuditCategory } from '@jurivoxa/shared';

export interface AuditLogActor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  memberType: string;
  avatarUrl: string | null;
}

export interface AuditLogEntry {
  id: string;
  chambersId: string;
  actorId: string | null;
  actorEmail: string | null;
  // Was an 11-value union against a 67-value enum, so anything specific — fee_note_sent,
  // conflict_override_approved — was a type error waiting to happen and silently rendered raw.
  // AuditActionValue is generated from the shared taxonomy and covers every enum value.
  action: AuditActionValue;
  entityType: string | null;
  entityId: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  ip: string | null;
  userAgent: string | null;
  sequenceNumber: string | null;
  createdAt: string;
  actor: AuditLogActor | null;
}

export interface AuditLogPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ListAuditLogsResponse {
  logs: AuditLogEntry[];
  pagination: AuditLogPagination;
}

export interface AuditLogSummary {
  today: number;
  thisWeek: number;
  byType: Array<{
    entityType: string | null;
    action: string;
    count: number;
  }>;
}

export interface ListAuditLogsParams {
  page?: number;
  limit?: number;
  entityType?: string;
  entityId?: string;
  actorId?: string;
  action?: AuditActionValue;
  /** Two-level filter: a category on its own, or narrowed by `action`. */
  category?: AuditCategory;
  from?: string;
  to?: string;
  search?: string;
}

export async function fetchAuditLogs(params: ListAuditLogsParams = {}): Promise<ListAuditLogsResponse> {
  const res = await api.get<ListAuditLogsResponse>('/audit-log', { params });
  return res.data;
}

/**
 * Download the current filter as CSV.
 *
 * The file is built by the server from the SAME scoped query as the list, so it can never hold
 * a row the user cannot see on screen. Fetched as a blob rather than navigated to, because the
 * endpoint needs the Authorization header that a plain <a href> cannot carry.
 */
export async function downloadAuditLogCsv(
  params: Omit<ListAuditLogsParams, 'page' | 'limit'> = {},
): Promise<{ truncated: boolean; rowCount: number }> {
  const res = await api.get('/audit-log/export', { params, responseType: 'blob' });

  const stamp = new Date().toISOString().slice(0, 10);
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `activity-log-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers; one tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1_000);

  return {
    truncated: res.headers['x-truncated'] === 'true',
    rowCount: Number(res.headers['x-row-count'] ?? 0),
  };
}

export async function fetchAuditLogSummary(mine = false): Promise<AuditLogSummary> {
  const res = await api.get<AuditLogSummary>('/audit-log/summary', { params: { mine } });
  return res.data;
}

export async function fetchRecentActivity(options: {
  mine?: boolean;
  limit?: number;
  entityTypes?: string[];
} = {}): Promise<{ logs: AuditLogEntry[] }> {
  const params: Record<string, string> = {};
  if (options.mine) params.mine = 'true';
  if (options.limit) params.limit = String(options.limit);
  if (options.entityTypes?.length) params.entityTypes = options.entityTypes.join(',');

  const res = await api.get<{ logs: AuditLogEntry[] }>('/audit-log/recent', { params });
  return res.data;
}
